import React, { useRef } from 'react'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { Save } from '@material-ui/icons'
import * as moment from 'moment'
import {
  Button,
  CircularProgress,
  TextField,
  Typography,
  Select,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  MenuItem,
  Collapse,
  InputAdornment
} from '@material-ui/core'
import { isNotIntegerNumber } from 'views/utils/functions'
import { useStyles } from './OrganizerForm.style'

const validationSchema = Yup.object().shape({
  Name: Yup.string()
    .required('Es requerido ingresar un nombre')
    .max(250, "La cantidad máxima de caracteres es de '250'"),
  Commission: Yup.number()
    .required('Es requerido ingresar la comisión')
    .moreThan(0, 'La Comisión debe ser mayor a 0'),
  FacebookPixelId: Yup.string().max(
    20,
    "La cantidad máxima de caracteres es de '20'"
  ),
  FacebookAccessToken: Yup.string()
    .test(
      'min-length',
      "La cantidad mínima de caracteres es de '50'",
      (value) => !value || value.length >= 50
    )
    .max(300, "La cantidad máxima de caracteres es de '300'")
    .matches(/^\S*$/, 'El token no puede contener espacios'),
  TaxIdentificationNumber: Yup.string()
    .required('Es requerido ingresar el Cuit')
    .max(20, "La cantidad máxima de dígitos es '20'"),
  CustomerName: Yup.string()
    .required('Es requerido ingresar el Nombre')
    .max(100, "La cantidad máxima de caracteres es de '100'"),
  Address: Yup.string()
    .required('Es requerido ingresar la Dirección')
    .max(100, "La cantidad máxima de caracteres es de '100'"),
  Phone: Yup.string()
    .required('Es requerido ingresar un Teléfono')
    .max(20, "La cantidad máxima de dígitos es '20'"),
  Email: Yup.string()
    .required('Es requerido ingresar un Email')
    .email('Ingrese un email válido'),
  LandingSettingsSlug: Yup.string().when('IsLandingEnabled', {
    is: true,
    then: Yup.string()
      .required('Es requerido ingresar una URL personalizada')
      .min(3, 'La URL personalizada debe tener al menos 3 caracteres')
      .max(60, 'La URL personalizada no puede exceder los 60 caracteres')
      .test(
        'is-slug-valid',
        'La URL solo puede contener letras minúsculas, números y guiones medios. No puede empezar o terminar con guiones.',
        (value) => {
          if (!value) return false
          return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)
        }
      ),
    otherwise: Yup.string().nullable()
  })
})

const BASE_URL = process.env.REACT_APP_URL || ''

export const OrganizerForm = ({
  values,
  existingAccessToken,
  mpCommissions,
  mpAutorized,
  mpUserId,
  mpExpirationDate,
  actionText,
  handleSubmit,
  onCancel,
  actionPending,
  AutomaticInvoiceEnabled
}) => {
  const classes = useStyles()
  const originalToken = useRef(existingAccessToken || '')

  const createLabel = (label, value, isDate) => {
    return (
      <div className={classes.containerText}>
        <Typography variant='h6'>
          {label}:&nbsp;&nbsp;
          <Typography variant='h6' component='span' className={classes.value}>
            {isDate ? moment(value).format('DD/MM/YYYY HH:mm') : value}
          </Typography>
        </Typography>
      </div>
    )
  }

  const validateValues = (values) => {
    for (const key in values) {
      if (typeof values[key] === 'string') {
        values[key] = values[key].trim()
      }
    }
  }

  return (
    <Formik
      enableReinitialize={false}
      initialValues={values}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema}
      onSubmit={(vals, formikHelpers) => {
        if (!vals.IsLandingEnabled) {
          vals.LandingSettingsSlug = null
        }
        if (!vals.FacebookAccessToken && originalToken.current) {
          delete vals.FacebookAccessToken
        }
        return handleSubmit(vals, formikHelpers)
      }}>
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting
      }) => (
        <form
          noValidate
          onSubmit={handleSubmit}
          className={classes.formContainer}>
          <div className={classes.fieldsContainer}>
            <div className={classes.actionsContainerTop}>
              <Button
                onClick={onCancel}
                disabled={isSubmitting}
                color='primary'>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  validateValues(values)
                  handleSubmit()
                }}
                disabled={actionPending || isSubmitting}
                color='primary'
                variant='contained'
                startIcon={<Save />}>
                {actionPending || isSubmitting ? (
                  <CircularProgress size={24} color='secondary' />
                ) : (
                  actionText
                )}
              </Button>
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.Name && touched.Name && errors.Name) ||
                  'Nombre del Organizador'
                }
                name='Name'
                value={values.Name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Name && Boolean(errors.Name)}
                fullWidth
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.Commission &&
                    touched.Commission &&
                    errors.Commission) ||
                  '% Comisión ticketPass'
                }
                name='Commission'
                type='number'
                value={values.Commission}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Commission && Boolean(errors.Commission)}
                fullWidth
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.FacebookPixelId &&
                    touched.FacebookPixelId &&
                    errors.FacebookPixelId) ||
                  'Facebook Pixel Id'
                }
                name='FacebookPixelId'
                value={values.FacebookPixelId}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyPress={(e) => isNotIntegerNumber(e)}
                onKeyDown={(e) => isNotIntegerNumber(e)}
                error={
                  touched.FacebookPixelId && Boolean(errors.FacebookPixelId)
                }
                fullWidth
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.FacebookAccessToken &&
                    touched.FacebookAccessToken &&
                    errors.FacebookAccessToken) ||
                  (originalToken.current && !values.FacebookAccessToken
                    ? 'Token de acceso (configurado)'
                    : 'Token de acceso a la API de Conversiones (Facebook)')
                }
                name='FacebookAccessToken'
                value={values.FacebookAccessToken}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.FacebookAccessToken &&
                  Boolean(errors.FacebookAccessToken)
                }
                fullWidth
                size='small'
                variant='outlined'
                className={`${classes.field} ${classes.accessTokenField}`}
                placeholder={
                  originalToken.current
                    ? 'Dejar vacío para mantener el token actual'
                    : 'EAAGm0ZCZAHkZBgBAPZBpZCP5...'
                }
              />
            </div>
            <FormControl
              variant='outlined'
              className={`${classes.fieldRow} ${classes.selectRow}`}>
              <InputLabel className={classes.selectLabelStyle}>
                Comisión MP por defecto
              </InputLabel>
              <Select
                name='DefaultCommissionMp'
                value={values.DefaultCommissionMp}
                className={classes.selectStyle}
                fullWidth
                required
                onChange={handleChange}
                onBlur={handleBlur}
                size='small'
                label='DefaultCommissionMp'>
                {mpCommissions &&
                  !mpCommissions.find(
                    (c) => c.Fee === values.DefaultCommissionMp
                  ) && (
                    <MenuItem value={values.DefaultCommissionMp}>
                      {values.DefaultCommissionMp}% (Comisión MP No Vigente)
                    </MenuItem>
                  )}
                {mpCommissions &&
                  mpCommissions.map((c) => (
                    <MenuItem key={c.Fee} value={c.Fee}>
                      {c.TypeFee}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Typography className={classes.billingLabel} variant='h6'>
              Datos de facturación
            </Typography>
            <hr className={classes.separator} />

            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.TaxIdentificationNumber &&
                    touched.TaxIdentificationNumber &&
                    errors.TaxIdentificationNumber) ||
                  'CUIT'
                }
                name='TaxIdentificationNumber'
                value={values.TaxIdentificationNumber}
                onChange={handleChange}
                required
                onBlur={handleBlur}
                autoComplete='off'
                inputProps={{ maxLength: 13 }}
                onKeyPress={(e) => isNotIntegerNumber(e)}
                onKeyDown={(e) => isNotIntegerNumber(e)}
                error={
                  touched.TaxIdentificationNumber &&
                  Boolean(errors.TaxIdentificationNumber)
                }
                fullWidth
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.CustomerName &&
                    touched.CustomerName &&
                    errors.CustomerName) ||
                  'Nombre'
                }
                name='CustomerName'
                required
                value={values.CustomerName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.CustomerName && Boolean(errors.CustomerName)}
                fullWidth
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <FormControl
              variant='outlined'
              className={`${classes.fieldRow} ${classes.selectRow}`}>
              <InputLabel className={classes.selectLabelStyle}>
                tipo de persona
              </InputLabel>
              <Select
                labelId='CustomerType-label'
                name='CustomerType'
                id='ticketId-select'
                value={values?.CustomerType || 0}
                className={classes.selectStyle}
                fullWidth
                required
                onChange={handleChange}
                size='small'
                label={'CustomerType'}>
                <MenuItem value={0}>Persona Física</MenuItem>
                <MenuItem value={1}>Persona Jurídica</MenuItem>
              </Select>
            </FormControl>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.Address && touched.Address && errors.Address) ||
                  'Dirección'
                }
                name='Address'
                required
                value={values.Address}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Address && Boolean(errors.Address)}
                fullWidth
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.Phone && touched.Phone && errors.Phone) || 'Teléfono'
                }
                name='Phone'
                value={values.Phone}
                required
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Phone && Boolean(errors.Phone)}
                fullWidth
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.Email && touched.Email && errors.Email) || 'Email'
                }
                name='Email'
                required
                value={values.Email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Email && Boolean(errors.Email)}
                fullWidth
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
          </div>
          <div className={classes.fieldRow} style={{ marginLeft: 16 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.IsLandingEnabled}
                  onChange={handleChange}
                  name='IsLandingEnabled'
                />
              }
              label='¿Habilitar landing?'
            />
          </div>
          <Collapse in={values.IsLandingEnabled} timeout='auto' unmountOnExit>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.LandingSettingsSlug &&
                    touched.LandingSettingsSlug &&
                    errors.LandingSettingsSlug) ||
                  'Url personalizada'
                }
                name='LandingSettingsSlug'
                value={values.LandingSettingsSlug}
                placeholder={
                  values.Name
                    ? values.Name.toLowerCase()
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .replace(/\s+/g, '-')
                        .replace(/[^a-z0-9-_]/g, '')
                    : 'mi-url-personalizada'
                }
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.LandingSettingsSlug &&
                  Boolean(errors.LandingSettingsSlug)
                }
                fullWidth
                size='small'
                variant='outlined'
                className={classes.field}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position='start'
                      style={{ marginRight: 1, marginTop: 0 }}>
                      {`${BASE_URL}/`}
                    </InputAdornment>
                  )
                }}
              />
            </div>
          </Collapse>
          {mpAutorized && (
            <>
              <hr className={classes.separator} />
              {createLabel('MercadoPago Autorizado', 'Si')}
              {createLabel('MercadoPago UserId', mpUserId)}
              {createLabel(
                'MercadoPago Fecha de Expiración',
                mpExpirationDate,
                true
              )}
            </>
          )}
          {AutomaticInvoiceEnabled != null &&
            createLabel(
              'Facturación Automática',
              `${AutomaticInvoiceEnabled ? 'Si' : 'No'}`
            )}
          <div className={classes.actionsContainer}>
            <Button onClick={onCancel} disabled={isSubmitting} color='primary'>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                validateValues(values)
                handleSubmit()
              }}
              disabled={actionPending || isSubmitting}
              color='primary'
              variant='contained'
              startIcon={<Save />}>
              {actionPending || isSubmitting ? (
                <CircularProgress size={24} color='secondary' />
              ) : (
                actionText
              )}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  )
}

export default OrganizerForm
